import { Grid } from '@mui/material';
import { TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { getBusinessContacts } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import { PortfolioValidation } from 'lib/types/models';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import { CustomHandle } from '..';
import DocumentsDropzone, { PendingDocument } from './Inputs/DocumentsDropzone';
import { MultiSelect } from './Inputs/MultiSelect';
import { useCreatePortfolio } from './useCreatePortfolio';

type Props = {
  businessIdentifier: string | undefined;
  validations: PortfolioValidation[];
};

const CustomForm = forwardRef<CustomHandle, Props>(
  ({ businessIdentifier, validations }, ref) => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user);
    const ownIdentifier = user?.businessIdentifier;
    const [title, setTitle] = useState<string>('');
    const [validUntil, setValidUntil] = useState<Dayjs | null>(null);
    const [ownUserIds, setOwnUserIds] = useState<number[]>([]);
    const [businessUserIds, setBusinessUserIds] = useState<number[]>([]);
    const [selectedValidations, setSelectedValidations] = useState<number[]>(
      [],
    );
    const [documents, setDocuments] = useState<PendingDocument[]>([]);
    const query = new URLSearchParams({
      isLegalRepresentative: 'true',
    }).toString();
    const { data: { items: ownUsers } = { items: [] } } = getBusinessContacts(
      dispatch,
      ownIdentifier,
      query,
    );
    const { data: { items: businessUsers } = { items: [] } } =
      getBusinessContacts(dispatch, businessIdentifier, query);

    const { handleCreatePortfolio } = useCreatePortfolio(
      businessIdentifier,
      documents,
      title,
      validUntil,
      selectedValidations,
      validations,
      ownUserIds,
      businessUserIds,
    );

    const toStringArray = (value: number[]) => value.map((id) => id.toString());
    const toNumberArray = (value: string[]) =>
      value.map((id) => Number(id)).filter((id) => !Number.isNaN(id));

    const mapOptions = (options: Contact[] | undefined) => {
      if (!options) return [];
      return options.map((option) => ({
        value: option.id.toString(),
        label: option.name ?? option.email,
      }));
    };

    useImperativeHandle(ref, () => ({
      submit: handleCreatePortfolio,
    }));

    return (
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            margin="dense"
            label="Título del portafolio"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ingrese el título del portafolio"
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Válido hasta"
              value={validUntil}
              onChange={(newValue) => setValidUntil(newValue)}
              minDate={dayjs().startOf('day')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'dense',
                  placeholder: 'Seleccione la fecha de vencimiento',
                },
              }}
            />
          </LocalizationProvider>

          <MultiSelect
            options={mapOptions(ownUsers)}
            value={toStringArray(ownUserIds)}
            onChange={(value) => setOwnUserIds(toNumberArray(value))}
            label="Tus firmantes"
            max={2}
          />
          <MultiSelect
            options={mapOptions(businessUsers)}
            value={toStringArray(businessUserIds)}
            label="Firmantes del cliente"
            onChange={(value) => setBusinessUserIds(toNumberArray(value))}
            max={2}
          />

          <MultiSelect
            options={validations.map((v) => ({
              value: v.id.toString(),
              label: v.description ?? '',
            }))}
            value={toStringArray(selectedValidations)}
            label="Validaciones del portafolio"
            onChange={(value) => setSelectedValidations(toNumberArray(value))}
          />
        </Grid>
        <Grid item xs={6}>
          <DocumentsDropzone documents={documents} onChange={setDocuments} />
        </Grid>
      </Grid>
    );
  },
);

CustomForm.displayName = 'CustomForm';
export default CustomForm;
